import { createContext, type ReactNode, useContext, useEffect, useSyncExternalStore } from 'react'
import type { PlatformSessionStore, SessionSnapshot } from '../lib/session.ts'

/**
 * The session, as React sees it.
 *
 * The store is the authority and lives outside React (`lib/session.ts`); this is the
 * subscription. `useSyncExternalStore` rather than state-in-a-provider because the
 * transport's recovery hook writes to the store from outside any render, and a token
 * rotation that React never heard about would leave the screen claiming a session that
 * had already ended.
 */
const SessionContext = createContext<PlatformSessionStore | undefined>(undefined)

export type SessionProviderProps = {
  readonly store: PlatformSessionStore
  readonly children: ReactNode
}

export function SessionProvider({ store, children }: SessionProviderProps) {
  // The idle clock is armed on real interaction only — a background refetch is not the
  // owner being at the desk (§4.4). Registered here so it exists exactly as long as the
  // application does.
  useEffect(() => {
    const note = () => {
      store.noteActivity()
    }
    const events = ['pointerdown', 'keydown', 'wheel'] as const
    for (const event of events) {
      window.addEventListener(event, note, { passive: true })
    }

    return () => {
      for (const event of events) {
        window.removeEventListener(event, note)
      }
      store.dispose()
    }
  }, [store])

  return <SessionContext.Provider value={store}>{children}</SessionContext.Provider>
}

/** The store itself, for the handful of places that call it rather than read it. */
export function useSessionStore(): PlatformSessionStore {
  const store = useContext(SessionContext)
  if (store === undefined) {
    // Deliberately a throw rather than a fallback: a component rendering outside the
    // provider would otherwise quietly behave as if nobody were signed in.
    throw new Error('useSessionStore must be used inside a SessionProvider')
  }

  return store
}

/** The current session state, re-rendering whatever reads it when it changes. */
export function useSession(): SessionSnapshot {
  const store = useSessionStore()
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
}
